/**
 * Page-state editor (the rail's "Stato pagina" AREA) — the SINGLE registry for
 * the page's `$page` variables.
 *
 * Thin wrapper over the shared `StateScopeEditor` (state-scope unification, the
 * `$global` twin is `GlobalStateEditor`) — it adapts the id-keyed
 * `PageVariable[]` onto `ScopeEntry` and injects the two PAGE-ONLY extras the
 * shell doesn't have: the **"from components"** promote list (undeclared `$page`
 * keys an input's `state_key` / a data_list's `select_into` write at runtime) and
 * the **usage badge** (the per-page read/write graph). `$page` is now a TYPED
 * declared bag too (`showType`) — parity with `$global`.
 *
 * The rule (see docs): components reference variables, they don't create state
 * out of thin air. Anything writing `$page` should land in this registry. `$page`
 * is page-scoped — it DIES on navigation (the runtime keeps it in React state),
 * unlike `$global` which survives within the portal session.
 */
import { Variable } from "lucide-react";

import { genId } from "@/lib/pageTree";
import { consumerLabels, type PageReferences, type Usage } from "@/lib/pageReferences";
import type { PageVariable } from "@/lib/pageTypes";

import { StateScopeEditor, type ScopeEntry } from "./StateScopeEditor";
import { UsageBadge } from "./UsageBadge";

type AreaView = "list" | "detail" | "full";

export function StateEditor({
  variables,
  stateKeys,
  references,
  onJump,
  onChange,
  view = "full",
  selectedId,
  onSelectId,
}: {
  variables: PageVariable[];
  /** Implicit `$page` keys written by components (input `state_key` /
   *  data_list `select_into`) — surfaced read-only, promotable. */
  stateKeys: string[];
  /** Static "where used" index — enriches the delete confirm with the actual
   *  components/queries that read or write the variable, and powers the badge. */
  references?: PageReferences;
  /** Jump to a consumer from the usage badge (select node / switch query). */
  onJump?: (u: Usage) => void;
  onChange: (variables: PageVariable[]) => void;
  view?: AreaView;
  /** Controlled selection (master/detail split). Omit for the legacy single pane. */
  selectedId?: string | null;
  onSelectId?: (id: string | null) => void;
}) {
  const declaredNames = new Set(variables.map((v) => v.name));
  const undeclared = stateKeys.filter((k) => k && !declaredNames.has(k));
  const scopeEntries: ScopeEntry[] = variables.map((v) => ({
    key: v.id,
    name: v.name,
    type: v.type,
    initial: v.initial,
  }));

  const controlled = onSelectId !== undefined;

  /** Append a declared variable (de-duping the name) and return its id so the
   *  shell selects it. `name` pre-fills it when promoting an undeclared key. */
  const add = (name = ""): string => {
    const id = genId(new Set(variables.map((v) => v.id)));
    const base = name || "var";
    let n = base;
    let i = 1;
    while (declaredNames.has(n)) n = `${base}${++i}`;
    onChange([...variables, { id, name: n, initial: null }]);
    return id;
  };
  const patch = (id: string, next: Partial<ScopeEntry>) =>
    onChange(
      variables.map((v) => {
        if (v.id !== id) return v;
        const merged: PageVariable = { ...v };
        if (next.name !== undefined) merged.name = next.name;
        // The shared shell clears a type with `null`; `PageVariable.type` is
        // `StateValueType | undefined` — normalise.
        if ("type" in next) merged.type = next.type ?? undefined;
        if ("initial" in next) merged.initial = next.initial;
        return merged;
      }),
    );
  const remove = (entry: ScopeEntry) => {
    // Delete-guard: list the components/queries that read or write this var so
    // the author knows what will break. References include the opaque caveat —
    // a custom_component / js query may touch $page.<name> invisibly.
    const consumers = consumerLabels(references?.var[entry.name]);
    const writtenByComponent = stateKeys.includes(entry.name);
    let msg: string;
    if (consumers.length) {
      msg = `La variabile "${entry.name}" è usata da: ${consumers.join(", ")}.\nEliminarne la dichiarazione comunque?`;
    } else if (references?.hasOpaque) {
      msg = `La variabile "${entry.name}" non risulta usata staticamente, ma la pagina ha un componente custom o una query JS che potrebbe leggerla.\nEliminarla comunque?`;
    } else if (writtenByComponent) {
      msg = `La variabile "${entry.name}" è scritta da un componente. Eliminarne la dichiarazione (perderà il valore iniziale)?`;
    } else {
      msg = `Eliminare la variabile "${entry.name}"?`;
    }
    if (window.confirm(msg)) onChange(variables.filter((x) => x.id !== entry.key));
  };

  return (
    <StateScopeEditor
      entries={scopeEntries}
      icon={Variable}
      listTitle="Variabili"
      addLabel="Nuova variabile"
      emptyListLabel="Nessuna variabile dichiarata."
      nameAriaLabel="Nome variabile"
      deleteTitle="Elimina variabile"
      deleteLabel="Elimina variabile"
      emptyDetailHint={<p>Seleziona una variabile o aggiungine una.</p>}
      showType
      view={view}
      selectedKey={controlled ? (selectedId ?? null) : undefined}
      onSelectKey={controlled ? onSelectId : undefined}
      onAdd={() => add()}
      onPatch={patch}
      onRemove={remove}
      detailFooter={
        references
          ? (entry) => (
              <UsageBadge
                usages={references.var[entry.name] ?? []}
                hasOpaque={references.hasOpaque}
                onJump={onJump}
              />
            )
          : undefined
      }
      listExtra={
        undeclared.length > 0
          ? ({ select }) => (
              <div className="mt-2 border-t border-border/50 pt-2">
                <div className="mb-1 px-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                  Da componenti (sola lettura)
                </div>
                <div className="flex flex-col gap-1">
                  {undeclared.map((k) => (
                    <div key={k} className="flex items-center gap-1.5 px-1">
                      <code className="min-w-0 flex-1 truncate font-mono text-[11px] text-muted-foreground">
                        {k}
                      </code>
                      <button
                        type="button"
                        className="shrink-0 rounded border border-border px-1.5 py-0.5 text-[10px] text-accent hover:bg-surface"
                        title="Registra come variabile dichiarata"
                        onClick={() => select(add(k))}
                      >
                        promuovi
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )
          : undefined
      }
    />
  );
}
